// POI-Symbol-Layer-Helpers
// =========================
//
// Zentrale Stelle für die Drinking-Water-POIs auf dem Map-Canvas. Wird von
// MapPage UND CreateCustomPage benutzt — beide rendern dieselben blau-weissen
// 💧-Pins, beide reagieren auf dieselbe `usePoiDrinkingWater`-Preference, der
// User toggelt einmalig und sieht die POIs überall wo eine Map ist.
//
// Stage 6m-fix: extrahiert aus pages/MapPage.tsx, damit CreateCustomPage
// dieselben Helpers ohne Code-Duplikation nutzen kann (User-Wunsch:
// "im planen / navi modus sollte ich auch die POIs auswählen können").

import type maplibregl from 'maplibre-gl';
import { POI_CATEGORIES } from '../poi';

/**
 * Eindeutige Namen für Icon / Source / Layer. Eigene `adt-`-Präfixe, damit wir
 * nicht mit Basemap-Layern kollidieren.
 */
export const POI_DW_ICON = 'adt-poi-drinking-water-icon';
export const POI_DW_SOURCE = 'adt-poi-drinking-water';
export const POI_DW_LAYER = 'adt-poi-drinking-water-symbols';

/**
 * Ab diesem Zoom-Level fangen wir an, Drinking-Water-POIs überhaupt zu rendern.
 *
 * Wave 14ca-fix10 (User-Wunsch): Minzoom von 12 auf 10 gesenkt — der User will
 * Wasserstellen schon bei größerer Übersicht sehen, um Routen daran entlang
 * planen zu können. Bei 10 sind in Liguria typischerweise 5-30 Marker auf
 * dem Screen, das ist noch lesbar (allowOverlap macht's robust gegen lokale
 * Cluster). Darunter wird's wirklich unleserlicher Blob.
 */
export const POI_MIN_ZOOM = 10;

/**
 * Zeichnet das Drinkwater-Icon (Sky-500-Kreis + weißer Rand + 💧-Emoji) in
 * einen HiDPI-Canvas und registriert's als Map-Icon. Idempotent.
 *
 * Größe-Logik: wir zeichnen auf 52px mit pixelRatio:2 → MapLibre rendert's
 * bei `icon-size: 1` als 26px-CSS-Sprite.
 */
export function ensurePoiIcon(map: maplibregl.Map): void {
  if (map.hasImage(POI_DW_ICON)) return;
  const SIZE = 52;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const radius = SIZE / 2 - 4;
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;

  ctx.fillStyle = 'rgba(14,165,233,0.95)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font =
    '28px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  ctx.fillText(POI_CATEGORIES.drinking_water.emoji, cx, cy + 1);

  const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
  map.addImage(POI_DW_ICON, imageData as unknown as HTMLImageElement, {
    pixelRatio: 2,
  });
}

/** Idempotent: Source + Layer anlegen falls nicht vorhanden. */
export function ensurePoiSourceAndLayer(map: maplibregl.Map): void {
  if (!map.getSource(POI_DW_SOURCE)) {
    map.addSource(POI_DW_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(POI_DW_LAYER)) {
    map.addLayer({
      id: POI_DW_LAYER,
      type: 'symbol',
      source: POI_DW_SOURCE,
      minzoom: POI_MIN_ZOOM,
      layout: {
        'icon-image': POI_DW_ICON,
        'icon-size': 1,
        // Alle Icons zeigen, auch wenn sie sich überlappen würden.
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
}

/** Setzt Source-Data idempotent; wirft nicht wenn die Source noch nicht da ist. */
export function setPoiData(
  map: maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.Point>[],
): void {
  const src = map.getSource(POI_DW_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (!src) return;
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Tear-down: Layer + Source entfernen, Icon bleibt für nächste Aktivierung.
 *
 * Stage 6m-fix3: defensive try-catch. Die useEffect-Cleanup-Reihenfolge in
 * React 18 ist nicht garantiert synchron-reverse — bei Page-Unmount
 * (z. B. NAVI → MAP) kann der Main-Map-Cleanup `map.remove()` bereits
 * gelaufen sein bevor mein POI-Cleanup hier landet. `map.getLayer()` greift
 * dann auf `this.style.getLayer` zu, aber `this.style` ist undefined →
 * uncaught TypeError → React zerlegt seinen Render-Tree → schwarzer Screen.
 * User-Bug-Report 14ce-fix3: "wenn ich im NAVI menü bin, dann auf map klicke,
 * wird die ganze app schwarz". Die destroyed-map hat eh keine Resources mehr
 * zu entfernen — wir schlucken den Throw und sind fein.
 */
export function removePoiSymbol(map: maplibregl.Map): void {
  try {
    if (map.getLayer(POI_DW_LAYER)) map.removeLayer(POI_DW_LAYER);
    if (map.getSource(POI_DW_SOURCE)) map.removeSource(POI_DW_SOURCE);
  } catch {
    // Map already destroyed — nothing to clean up. Don't propagate.
  }
}
